import { ResizeNotifier, prefersReducedMotion } from '@theme/utilities';
import { Component } from '@theme/component';

class JumboText extends Component {
  #scrollListener = null;
  #scrollSyncStarted = false;

  connectedCallback() {
    super.connectedCallback();
    this.#setIntersectionObserver();
    window.addEventListener('resize', this.#windowResizeListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    if (this.#scrollListener) window.removeEventListener('scroll', this.#scrollListener);
    window.removeEventListener('resize', this.#windowResizeListener);
  }

  #firstResize = true;

  #setIntersectionObserver() {
    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        if (!entry) return;

        if (entry.isIntersecting && this.#firstResize) {
          this.#handleResize(entry.boundingClientRect.width);
        }

        if (this.dataset.textEffect && this.dataset.textEffect !== 'none' && !prefersReducedMotion()) {
          if (entry.isIntersecting) {
            this.classList.add('ready');
            if (!this.#scrollSyncStarted) {
              this.#scrollSyncStarted = true;
              this.#startScrollSync();
            }
          } else {
            this.classList.remove('ready', 'jumbo-text-visible');
          }
        }
      },
      { threshold: 0 }
    );

    this.#intersectionObserver?.observe(this);
  }

  #calculateOptimalFontSize = (containerWidth) => {
    const { widestChild: firstPassWidestChild, widestChildWidth: firstPassWidestChildWidth } = this.#findWidestChild();
    if (!firstPassWidestChild || !firstPassWidestChildWidth) return;

    const currentFontSize = parseFloat(window.getComputedStyle(firstPassWidestChild).fontSize);
    const firstPassFontSize = Math.round(((currentFontSize * containerWidth) / firstPassWidestChildWidth) * 100) / 100;

    this.#resizeObserver.disconnect();
    this.style.fontSize = this.#clampFontSize(firstPassFontSize);

    const { widestChild: secondPassWidestChild, widestChildWidth: secondPassWidestChildWidth } = this.#findWidestChild();
    if (!secondPassWidestChild || !secondPassWidestChildWidth) return;

    const secondPassFontSize =
      Math.floor(((firstPassFontSize * containerWidth) / secondPassWidestChildWidth) * 100) / 100 - 0.15;

    if (secondPassFontSize !== firstPassFontSize) {
      this.style.fontSize = this.#clampFontSize(secondPassFontSize);
    }

    this.classList.add('ready');
    this.#resizeObserver.observe(this);
  };

  #findWidestChild = () => {
    let widestChild = null;
    let widestChildWidth = 0;
    for (const child of this.children) {
      if (!(child instanceof HTMLElement)) continue;
      const { width: childWidth } = child.getBoundingClientRect();
      if (!widestChild || childWidth > widestChildWidth) {
        widestChildWidth = childWidth;
        widestChild = child;
      }
    }
    return { widestChild, widestChildWidth };
  };

  #clampFontSize = (fontSize) => {
    const minFontSize = 1;
    const maxFontSize = 500;
    return `${Math.min(Math.max(fontSize, minFontSize), maxFontSize)}px`;
  };

  #handleResize = (containerWidth = undefined) => {
    if (!this.textContent?.trim()) return;
    if (containerWidth === undefined) containerWidth = this.offsetWidth;
    if (containerWidth <= 0) return;

    if (!this.#firstResize) {
      this.classList.remove('ready');
      this.style.fontSize = '';
    }

    this.#calculateOptimalFontSize(containerWidth);
    this.#firstResize = false;

    if (this.dataset.capText === 'true') return;

    const allSections = Array.from(document.querySelectorAll('.shopify-section'));
    const lastSection = allSections[allSections.length - 1];
    if (lastSection && !lastSection.contains(this)) return;

    const rect = this.getBoundingClientRect();
    const bottom = rect.bottom + window.scrollY;
    const distanceFromBottom = document.documentElement.offsetHeight - bottom;
    this.dataset.capText = (distanceFromBottom <= 100).toString();
  };

  #startScrollSync() {
    const lines = this.querySelectorAll('.jumbo-text-line');
    if (!lines.length) return;

    const updateProgress = () => {
      const rect = this.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      // Inizia quando il bordo inferiore entra nel viewport (rect.bottom > 0)
      // Finisce quando il bordo superiore raggiunge il centro dello schermo
 const start = windowHeight * 1.5;
const end = windowHeight * 0.8;


      const progress = Math.min(1, Math.max(0, (start - rect.top) / (start - end)));

      lines.forEach((line, i) => {
        const lineProgress = Math.min(1, Math.max(0, (progress - i * 0.12) / 0.6));
        line.style.clipPath = `inset(0 ${(1 - lineProgress) * 100}% 0 0)`;
        line.style.transition = 'none';
      });

      if (progress >= 1) {
        this.classList.add('jumbo-text-visible');
      } else {
        this.classList.remove('jumbo-text-visible');
      }
    };

    this.#scrollListener = updateProgress;
    window.addEventListener('scroll', this.#scrollListener, { passive: true });
    updateProgress();
  }

  #windowResizeListener = () => this.#handleResize();

  #resizeObserver = new ResizeNotifier((entries) => this.#handleResize(entries[0]?.borderBoxSize?.[0]?.inlineSize));

  #intersectionObserver = null;
}

if (!customElements.get('jumbo-text')) {
  customElements.define('jumbo-text', JumboText);
}
